import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import PublicLayout from "@/components/PublicLayout";
import { api } from "@/lib/api";
import { Search, ArrowRight, Calendar } from "lucide-react";

export default function Blog() {
  const [blogs, setBlogs] = useState([]);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("All");

  useEffect(() => { api.get("/blogs").then((r) => setBlogs(r.data)).catch(() => {}); }, []);

  const categories = useMemo(() => ["All", ...Array.from(new Set(blogs.map((b) => b.category)))], [blogs]);

  const filtered = useMemo(() => blogs.filter((b) => {
    const okCat = cat === "All" || b.category === cat;
    const okQ = !q || (b.title + " " + b.excerpt + " " + (b.tags || []).join(" ")).toLowerCase().includes(q.toLowerCase());
    return okCat && okQ;
  }), [blogs, q, cat]);

  const fmt = (iso) => iso ? new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "";

  return (
    <PublicLayout>
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-16 pb-8">
        <div className="text-xs uppercase tracking-widest text-blue-700">Insights</div>
        <h1 className="h-display mt-3 text-5xl md:text-6xl text-slate-900 max-w-4xl">Notes on tax, compliance & running a good business.</h1>
        <p className="mt-5 max-w-2xl text-slate-600 leading-relaxed">Practical, jargon-free reads for founders, MSMEs and salaried professionals — written by our senior team.</p>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex flex-col md:flex-row md:items-center gap-4">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
            <input data-testid="blog-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search articles…" className="w-full pl-11 pr-4 py-3 rounded-full border border-slate-200 bg-white text-sm focus:border-blue-500 focus:outline-none" />
          </div>
          <div className="flex flex-wrap gap-2">
            {categories.map((c) => (
              <button key={c} data-testid={`blog-filter-${c.toLowerCase().replace(/\s+/g, "-")}`} onClick={() => setCat(c)} className={`px-4 py-2 rounded-full text-sm border transition-colors ${cat === c ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200 hover:border-blue-400 hover:text-blue-700"}`}>{c}</button>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map((b) => (
            <Link key={b.id} to={`/blog/${b.slug}`} data-testid={`blog-card-${b.slug}`} className="group block rounded-2xl bg-white border border-slate-200 overflow-hidden hover:border-blue-400 hover:-translate-y-0.5 transition-transform duration-300">
              {b.cover_image && (
                <div className="aspect-[16/9] overflow-hidden bg-slate-100">
                  <img src={b.cover_image} alt={b.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
              )}
              <div className="p-6">
                <div className="text-xs uppercase tracking-widest text-blue-700">{b.category}</div>
                <h3 className="mt-2 text-xl font-medium text-slate-900 group-hover:text-blue-700 transition-colors leading-snug">{b.title}</h3>
                <p className="mt-3 text-sm text-slate-600 leading-relaxed line-clamp-3">{b.excerpt}</p>
                <div className="mt-5 flex items-center justify-between text-xs text-slate-500">
                  <span className="inline-flex items-center gap-1"><Calendar size={12} /> {fmt(b.published_at || b.created_at)}</span>
                  <span className="inline-flex items-center gap-1 text-blue-700">Read <ArrowRight size={12} /></span>
                </div>
              </div>
            </Link>
          ))}
          {filtered.length === 0 && (
            <div className="col-span-full text-center text-slate-500 py-16">No posts yet. Check back soon.</div>
          )}
        </div>
      </section>
    </PublicLayout>
  );
}
